import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, ViewEncapsulation, DestroyRef, ChangeDetectorRef, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, ReactiveFormsModule, FormGroup, FormControl } from '@angular/forms';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { emailValidator, phoneValidator, strongPasswordValidator, passwordMatchValidator } from '../../common/validators/form.validator';
import { UserService } from '../../services/user.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastrService } from 'ngx-toastr';
import { WirePhoneInputComponent } from '../../common/components/wire-phone-input/wire-phone-input.component';
import { SeoService } from '../../services/meta.service';
@Component({
  selector: 'wire-registration',
  imports: [RouterModule, CommonModule, ReactiveFormsModule, WirePhoneInputComponent],
  templateUrl: './registration.component.html',
  styleUrls: ['./registration.component.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegistrationComponent implements OnInit {
  public readonly userService = inject(UserService);
  public readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly toastr = inject(ToastrService);
  private readonly seoService = inject(SeoService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  public form = new UntypedFormGroup({
    fullName: new UntypedFormControl(null, [Validators.required]),
    email: new UntypedFormControl(null, [Validators.required, emailValidator, Validators.minLength(6)]),
    password: new UntypedFormControl(null, [Validators.required, strongPasswordValidator]),
    confirmPassword: new UntypedFormControl(null, [Validators.required]),
    phone: new FormGroup(
      {
        countryCode: new FormControl('+1', [Validators.required]),
        mobile: new FormControl(null, [Validators.required, phoneValidator])
      }
    ),
  }, { validators: passwordMatchValidator('password', 'confirmPassword') });

  initialValue = {
    countryCode: '+1',
    mobile: ''
  }
  public showPassword = false;
  public showConfirmPassword = false;
  public registratrationSuccess = false;
  public isIndustryType = false;
  private hash: string | null = null;
  public selectedFile: File | null = null;
  public selectedFilePreview: string | null = null;
  public fileSubmitAttempted: boolean = false;
  public readonly MAX_FILE_SIZE = 3 * 1024 * 1024;

  public get emailDomain() {
    return this.form.get('email')?.value?.split('@')[1];
  }
  ngOnInit(): void {
    this.seoService.setMetaTags({
      title: 'Wire Festival | Registration',
      description: 'Wire Festival Registration',
      keywords: 'Wire Festival Registration'
    });

    // Check for hash and email in query params
    // Use URLSearchParams which properly handles URL-encoded characters
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const email = urlParams.get('email');
      const hash = urlParams.get('hash');
      const type = urlParams.get('type');

      this.isIndustryType = type === 'industry';

      if (email) {
        if (hash) this.hash = hash;
        this.form.get('email')?.setValue(email);
        this.form.get('email')?.disable();
        this.cdr.detectChanges();
      }
    } else {
      // Fallback for SSR
      this.route.queryParams.subscribe(params => {
        const emailParam = params['email'];
        const hashParam = params['hash'];
        const typeParam = params['type'];

        this.isIndustryType = typeParam === 'industry';

        if (emailParam) {
          if (hashParam) this.hash = hashParam;
          this.form.get('email')?.setValue(emailParam);
          this.form.get('email')?.disable();
          this.cdr.detectChanges();
        }
      });
    }
  }
  handleFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) return;

    const validTypes = ['image/svg+xml', 'image/png', 'image/jpeg'];
    if (!validTypes.includes(file.type)) {
      this.toastr.error('Invalid file type. Please upload SVG, PNG, or JPG.');
      return;
    }

    if (file.size > this.MAX_FILE_SIZE) {
      this.toastr.error('File size exceeds 3MB limit.');
      return;
    }

    this.selectedFile = file;
    const reader = new FileReader();
    reader.onload = (e) => {
      this.selectedFilePreview = e.target?.result as string;
      this.cdr.detectChanges();
    };
    reader.readAsDataURL(file);
  }

  handlePhoneNumberChange(event: any) {
    this.form.get('phone')?.get('mobile')?.patchValue(event);
  }
  handleCountryChange(event: any) {
    this.form.get('phone')?.get('countryCode')?.setValue(event);
  }
  togglePasswordVisibility(field: 'password' | 'confirmPassword'): void {
    if (field === 'password') {
      this.showPassword = !this.showPassword;
    } else {
      this.showConfirmPassword = !this.showConfirmPassword;
    }
  }

  register(): void {
    this.fileSubmitAttempted = true;
    if (this.form.invalid || !this.selectedFile) return;
    const body = this.getBody()
    this.userService.register(body)
      .pipe(
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response: any) => {
          if (response?.access_token) {
            localStorage.setItem('auth_token', response.access_token);
            if (response.user) {
              this.userService.setUser(response.user);
            }

            if (response.user.isProfileCompleted) {
              this.router.navigate(['/profile/details']);
            } else {
              this.router.navigate(['/profile/user/personal']);
            }
            
            return;
          }
          this.registratrationSuccess = true;
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.toastr.error(error.error.message);
          console.error('Registration failed:', error);
        }
      });
  }

  getBody() {
    const formData = new FormData();
    formData.append('fullName', this.form.value.fullName);
    formData.append('email', this.form.get('email')?.disabled ? this.form.get('email')?.value : this.form.value.email);
    formData.append('password', this.form.value.password);
    formData.append('countryCode', this.form?.value?.phone?.countryCode);
    formData.append('mobile', this.form?.value?.phone?.mobile);

    if (this.hash) {
      formData.append('hash', this.hash);
    }

    if (this.selectedFile) {
      formData.append('avatar', this.selectedFile, this.selectedFile.name);
    }

    return formData;
  }
  
  goToDomain() {
    window.open(`https://${this.emailDomain}`, '_blank');
  }

  goToLogin() {
    this.router.navigate(['/login']);
  }

}